import { FC, useMemo } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Area,
} from 'recharts';
import { Box } from '@mui/material';
import { hexToRGBA } from 'shared/helpers/hexToRGBA';
import { TextColor } from 'shared/theme/colors';
import { LineChartData } from './types';

const getYAxisWidth = (max: number): number => {
  if (max < 100) return 12; // до 2 знаков
  if (max < 1000) return 16; // до 3 знаков
  if (max < 10000) return 24; // до 4 знаков
  return 30; // 5+ знаков
};

export const useAutoYAxisWidth = ({
  data,
  lineLabels,
}: {
  data: LineChartData[];
  lineLabels: string[];
}) => {
  const width = useMemo(() => {
    const values = data.flatMap((item) =>
      lineLabels
        .map((label) => item[label])
        .filter((v) => typeof v === 'number')
    ) as number[];

    const max = Math.max(...values, 0);
    return getYAxisWidth(max);
  }, [data, lineLabels]);

  return { yAxisWidth: width };
};

type AnalyticsLineChartProps = {
  data: LineChartData[];
  lineLabels: string[];
  lineColors: string[];
  minWidth: number;
  height: number;
};

const axisLineColor = '#8686861a';

type CustomTooltipProps = {
  active?: boolean;
  payload?: { color: string; name: string; value: number }[];
};

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: '#fff',
          padding: '16px',
          border: '1px solid #ddd',
          borderRadius: '8px',
        }}
      >
        {payload.map((point, index) => (
          <p
            key={`item-${index}`}
            style={{
              color: point.color,
              fontSize: '14px',
              fontWeight: 500,
              margin: '2px',
            }}
          >
            {point.name}{' '}
            <span style={{ color: TextColor.Primary, fontWeight: 600 }}>
              {point.value}
            </span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const AnalyticsLineChart: FC<AnalyticsLineChartProps> = ({
  data,
  lineLabels,
  lineColors,
  height = 500,
  minWidth,
}) => {
  const { yAxisWidth } = useAutoYAxisWidth({ data, lineLabels });

  return (
    <Box sx={{ width: '100%', minWidth: minWidth, height: height }}>
      <ResponsiveContainer>
        <AreaChart
          data={data}
          margin={{
            top: 0,
            right: 4,
            left: yAxisWidth,
            bottom: 0,
          }}
        >
          <CartesianGrid
            strokeWidth={1}
            stroke={axisLineColor}
            horizontal={false}
          />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={{ stroke: axisLineColor, strokeWidth: 1 }}
            tick={{
              fill: TextColor.Quaternary,
              fontSize: '12px',
              fontWeight: 500,
            }}
          />
          <YAxis
            tickLine={false}
            axisLine={{ stroke: axisLineColor }}
            width={yAxisWidth}
            domain={[0, 'dataMax + 4']}
            // tickCount={6}
            allowDecimals={false}
            interval="preserveStartEnd"
            tickFormatter={(value, index) => (index === 0 ? '' : value)}
            tick={{
              fill: TextColor.Quaternary,
              fontSize: '12px',
              fontWeight: 500,
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          {lineLabels.map((label, index) => (
            <Area
              key={label}
              type="linear"
              dataKey={label}
              stroke={lineColors[index]}
              fill={hexToRGBA(lineColors[index], 0.15)}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};
